import React, { useEffect, useRef, useState } from 'react';
import images from '../../../assets/images';
import './styles.scss';
import CustomButton from '../../customButton';

const FunnelBody = ({ data, steps, showNumbers, isLoading, onAdd, onRemove }) => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const [hoveredBar, setHoveredBar] = useState(null); // { index, x, y }
  const [selectedBar, setSelectedBar] = useState(null); // index of selected bar

  const allZero = Array.isArray(data) && data.length > 0 && data.every(d => d.value === 0);
  const hasData = Array.isArray(data)
    ? data.length > 0 && !allZero
    : data && typeof data === 'object'
    ? Object.keys(data).length > 0 && !allZero
    : !!data && !allZero;

  // Dynamically defines the maximum, rounding up to the nearest centesimal (100s) or milesimal (1000s)
  let maxDataValue = hasData ? Math.max(...data.map(d => d.value)) : 0;
  let stepBase = 100;
  if (maxDataValue >= 1000) {
    stepBase = 1000;
  }
  const maxValue = hasData ? Math.ceil(maxDataValue / stepBase) * stepBase : 0;
  // Minimum value is always zero
  const minValue = 0;

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && containerRef.current.parentElement) {
        const parent = containerRef.current.parentElement;
        setSize({
          width: parent.offsetWidth,
          height: parent.offsetHeight
        });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const ySteps = 5;

  // yLabels from minValue to maxValue
  const yLabels = Array.from({ length: ySteps + 1 }, (_, i) => {
    const val = minValue + (i * (maxValue - minValue)) / ySteps;
    return Math.round(val);
  });
  // Use site name for tooltip, fallback to displayName, then label
  const siteNames = Array.isArray(data) ? data.map(step => step.siteName || step.displayName || step.label || '') : [];
  const xLabels = Array.isArray(data) ? data.map(step => step.label || '') : [];

  // Extra space at the top to avoid cutting off values/labels
  const topPadding = 32;
  const bottomPadding = 56; // increased to avoid cropping X axis labels
  // Ensure SVG never exceeds 362px in height (body area)
  const maxChartHeight = 362;
  const svgHeight = size.height > 0 ? Math.min(size.height, maxChartHeight) : 260;
  // Use 100% of the container width, fallback to 800 if not measured yet
  const svgWidth = size.width > 0 ? size.width : 800;
  // Extra space on the left for Y axis numbers (increased to avoid cropping)
  const leftPadding = 80;

  const renderLoading = () => {
    return (
      <div className="funnel-body" ref={containerRef}>
        <div className={`placeholder loading`} style={{ backgroundImage: `url(${images['placeholder']})` }} />
      </div>
    );
  };

  const renderNoData = () => {
    // If all values are zero, show 'No data'
    if (allZero) {
      return (
        <div className="funnel-body" ref={containerRef} style={{ position: 'relative', width: '100%', height: 362 }}>
          <div className="empty">
            <p>No data found. Try different sites/steps or a different period.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="funnel-body" ref={containerRef} style={{ position: 'relative', width: '100%', height: 362 }}>
        <div className="empty">
          <p>{steps && steps.length === 0 ? 'Add steps to see the funnel' : `${steps.length} Steps Added`}</p>
          <div className="buttons">
            {steps && steps.length > 0 && (
              <CustomButton title="Clear Steps" classes="btn-danger btn-slim" handleClick={onRemove} />
            )}
            <CustomButton
              title={steps && steps.length === 0 ? 'Add Steps' : 'Edit Steps'}
              classes={'btn btn-primary btn-slim'}
              handleClick={onAdd}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderChart = () => {
    return (
      <div className="funnel-body" ref={containerRef} style={{ position: 'relative', width: '100%', height: 362 }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgWidth + leftPadding} ${svgHeight + topPadding + bottomPadding}`}
          preserveAspectRatio="none"
          style={{ display: 'block', width: '100%' }}
        >
          {/* Areas between the bars */}
          {size.width > 0 &&
            size.height > 0 &&
            data.length > 1 &&
            data.slice(0, -1).map((step, i) => {
              const nextStep = data[i + 1];
              const nBars = data.length;
              const barWidth = (svgWidth - leftPadding) / nBars / 2;
              // Left bar edges
              const xLeftEdge = leftPadding + (i + 0.5) * ((svgWidth - leftPadding) / nBars) - barWidth / 2;
              const xRightEdge = xLeftEdge + barWidth;
              const yTopLeftBar = topPadding + svgHeight - (step.value / maxValue) * svgHeight;
              const yBottomLeftBar = topPadding + svgHeight;
              // Right bar edges
              const xLeftEdgeNext = leftPadding + (i + 1 + 0.5) * ((svgWidth - leftPadding) / nBars) - barWidth / 2;
              const yTopRightBar = topPadding + svgHeight - (nextStep.value / maxValue) * svgHeight;
              const yBottomRightBar = topPadding + svgHeight;
              // Polygon between the bars: right edge of left bar (top/bottom), left edge of right bar (top/bottom)
              const points = `
                ${xRightEdge},${yTopLeftBar}
                ${xLeftEdgeNext},${yTopRightBar}
                ${xLeftEdgeNext},${yBottomRightBar}
                ${xRightEdge},${yBottomLeftBar}
              `;
              return <polygon key={`area-${i}`} points={points} fill="rgb(187,168,250)" opacity={0.15} />;
            })}
          {/** Config */}
          {/** Y Axis */}
          <line
            x1={leftPadding}
            x2={leftPadding}
            y1={topPadding}
            y2={topPadding + svgHeight}
            stroke="#888"
            strokeWidth={2}
          />

          {/** X Axis */}
          <line
            x1={leftPadding}
            x2={svgWidth + leftPadding}
            y1={topPadding + svgHeight}
            y2={topPadding + svgHeight}
            stroke="#888"
            strokeWidth={2}
          />

          {/** Horizontal grid lines and Y Axis labels */}
          {yLabels.map((val, i) => {
            const y = topPadding + svgHeight - (i * svgHeight) / ySteps;
            return (
              <g key={`y-${i}`}>
                <line x1={leftPadding} y1={y} x2={svgWidth + leftPadding} y2={y} stroke="#ddd" strokeWidth={1} />
                <text x={leftPadding - 10} y={y - 5} fontSize="12" fill="#666" textAnchor="end" fontWeight="bold">
                  {val.toLocaleString()}
                </text>
              </g>
            );
          })}

          {/** Bars of the chart */}
          {/* Remaining % between the bars */}
          {data.slice(0, -1).map((step, i) => {
            const nextStep = data[i + 1];
            const nBars = data.length;
            const barWidth = (svgWidth - leftPadding) / nBars / 2;
            // Center between the bars
            const xLeft = leftPadding + (i + 0.5) * ((svgWidth - leftPadding) / nBars) + barWidth / 2;
            const xRight = leftPadding + (i + 1 + 0.5) * ((svgWidth - leftPadding) / nBars) - barWidth / 2;
            const xCenter = (xLeft + xRight) / 2;
            // Vertical centering at 30% of the chart height, bottom to top
            const yCenter = topPadding + svgHeight - svgHeight * 0.3;
            // Percentage (remaining value)
            const percent = ((nextStep.value / step.value) * 100).toFixed(2);
            // Arrow
            const arrowWidth = 70;
            const arrowHeight = 28;
            const arrowX = xCenter - arrowWidth / 2;
            const arrowY = yCenter - arrowHeight / 2;
            return (
              <g key={`arrow-${i}`}>
                {/* Body of the arrow */}
                <rect
                  x={arrowX}
                  y={arrowY}
                  width={arrowWidth - 14}
                  height={arrowHeight}
                  fill="#888B9A"
                  opacity={0.85}
                />
                {/* Head of the arrow */}
                <polygon
                  points={`
              ${arrowX + arrowWidth - 14},${arrowY}
              ${arrowX + arrowWidth},${arrowY + arrowHeight / 2}
              ${arrowX + arrowWidth - 14},${arrowY + arrowHeight}
              `}
                  fill="#888B9A"
                  opacity={0.85}
                />
                {/* Percentage text */}
                <text
                  x={xCenter}
                  y={arrowY + arrowHeight / 2 + 2}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fontSize="16"
                  fill="#fff"
                  fontWeight="bold"
                >
                  {isNaN(percent) ? '--' : `${percent}%`}
                </text>
              </g>
            );
          })}
          {data.map((step, i) => {
            const nBars = data.length;
            const barWidth = (svgWidth - leftPadding) / nBars / 2;
            const barHeight = (step.value / maxValue) * svgHeight;
            const x = leftPadding + (i + 0.5) * ((svgWidth - leftPadding) / nBars) - barWidth / 2;
            const y = topPadding + svgHeight - barHeight;

            // Bar color logic
            let fillColor = 'rgb(47,104,229)';
            if (selectedBar === i) {
              fillColor = '#a9c8fa';
            } else if (hoveredBar && hoveredBar.index === i) {
              fillColor = '#5b8df6'; // lighter blue on hover
            }
            // No percent for tooltip now
            return (
              <g
                key={`bar-group-${i}`}
                onMouseEnter={e => {
                  const svgRect = containerRef.current.getBoundingClientRect();
                  const mouseX = e.clientX - svgRect.left;
                  const mouseY = e.clientY - svgRect.top;
                  setHoveredBar({
                    index: i,
                    x: mouseX,
                    y: mouseY,
                    barX: x,
                    barY: y,
                    barWidth,
                    barHeight,
                    label: steps && steps[i] && steps[i].displayName ? steps[i].displayName : siteNames[i],
                    value: step.value,
                    tooltipBorder: selectedBar === i ? '#a9c8fa' : 'rgb(47,104,229)'
                  });
                }}
                onMouseMove={e => {
                  if (hoveredBar && hoveredBar.index === i) {
                    const svgRect = containerRef.current.getBoundingClientRect();
                    const mouseX = e.clientX - svgRect.left;
                    const mouseY = e.clientY - svgRect.top;
                    setHoveredBar(prev => ({
                      ...prev,
                      x: mouseX,
                      y: mouseY,
                      tooltipBorder: selectedBar === i ? '#a9c8fa' : 'rgb(47,104,229)'
                    }));
                  }
                }}
                onMouseLeave={() => setHoveredBar(null)}
                onClick={() => setSelectedBar(i)}
              >
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={fillColor}
                  rx={4}
                  ry={4}
                  opacity={0.85}
                  style={{ cursor: 'pointer', transition: 'fill 0.15s' }}
                />
                {/* Value above the bar */}
                {showNumbers && (
                  <text
                    x={x + barWidth / 2}
                    y={y - 8}
                    textAnchor="middle"
                    fontSize="16"
                    fill="#243ed1"
                    fontWeight="bold"
                  >
                    {step.value.toLocaleString()}
                  </text>
                )}
              </g>
            );
          })}

          {/** X Axis labels */}
          {xLabels.map((label, i) => {
            const nBars = data.length;
            const x = leftPadding + (i + 0.5) * ((svgWidth - leftPadding) / nBars);
            // Center the label visually below the axis
            const y = topPadding + svgHeight + bottomPadding - 18; // 18 is approx font size
            return (
              <text
                key={`x-${i}`}
                x={x}
                y={y}
                textAnchor="middle"
                fontSize="18"
                fill="#333"
                alignmentBaseline="hanging"
              >
                {label}
              </text>
            );
          })}
        </svg>
        {/* Tooltip overlay */}
        {hoveredBar && (
          <div
            className="funnel-tooltip"
            style={{
              position: 'absolute',
              left: hoveredBar.x + 8,
              top: hoveredBar.y - 8,
              background: '#fff',
              border: `1px solid ${hoveredBar.tooltipBorder || 'rgb(47,104,229)'}`,
              borderRadius: 5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              padding: '4px 10px',
              pointerEvents: 'none',
              zIndex: 10,
              minWidth: 60,
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.2
            }}
          >
            <div style={{ fontSize: 12, color: '#444', marginBottom: 1 }}>{`Site: ${hoveredBar.label}`}</div>
            <div style={{ fontWeight: 'bold', fontSize: 16, color: '#222', display: 'inline-block' }}>
              {hoveredBar.value.toLocaleString()}
            </div>
          </div>
        )}
      </div>
    );
  };

  return <>{isLoading ? renderLoading() : hasData ? renderChart() : renderNoData()}</>;
};

export default FunnelBody;
